import { CanActivateFn } from '@angular/router';
import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';

/**
 * Wraps a `CanActivateFn` behind a dynamic import so its module (and transitive
 * deps like auth/tenant services) ship in a lazy chunk instead of the eager
 * initial bundle — public routes (e.g. `/:slug`) never download admin-only guards.
 * The injector is captured synchronously (valid injection context here) and reused
 * via `runInInjectionContext` when the real guard runs after the import resolves —
 * otherwise its `inject()` calls throw NG0203 (no active injection context).
 */
export function lazyCanActivate(loader: () => Promise<CanActivateFn>): CanActivateFn {
  return (route, state) => {
    const injector = inject(EnvironmentInjector);
    return from(loader()).pipe(
      switchMap((guard) => {
        const result = runInInjectionContext(injector, () => guard(route, state));
        return result instanceof Observable ? result : from(Promise.resolve(result));
      })
    );
  };
}
